/**
 * Загрузка выписок об имуществе в опись (решения от 25.09.2026): выписка ФНС
 * из ЕГРН, сведения Росреестра об объекте, сведения ГИБДД, сведения о счетах
 * (форма 9ф). Можно выбрать несколько файлов сразу — каждая строка ляжет
 * в свой раздел описи.
 *
 * Как и в остальных загрузках, сервер только предлагает строки: в дело
 * попадает отмеченное.
 *
 * ПОВТОРЫ МЕЖДУ ДОКУМЕНТАМИ. Один участок есть и в выписке ФНС, и в сведениях
 * Росреестра; одна машина — и в выписке ФНС, и в сведениях ГИБДД. Отмечается
 * одна копия: из Росреестра (там залоги) и из выписки ФНС (там вид ТС),
 * остальные приходят без отметки с пояснением.
 *
 * Прекращённые права и снятый с учёта транспорт в опись не предлагаются,
 * но те, что за последние три года, показываются на виду: это повод
 * проверить блок «Сделки».
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { BankAccount, Realty, Vehicle } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api, type PropertyImport } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';
import type { ImportHandle, Preloaded } from './import-handle.js';

const ruDate = (iso: string | null) => (iso ? iso.split('-').reverse().join('.') : '—');

/** Чьи строки главнее при повторе: Росреестр, выписка ФНС, ГИБДД, счета. */
const PRIORITY: Record<PropertyImport['kind'], number> = { rosreestr: 0, 'fns-taxpayer': 1, gibdd: 2, 'fns-accounts': 3 };

const REALTY_TITLES: Record<Realty['kind'], string> = {
  land: 'Земельный участок',
  house: 'Жилой дом',
  flat: 'Квартира',
  dacha: 'Дача',
  garage: 'Гараж',
  other: 'Иное',
};

export interface AcceptedProperty {
  realty: Realty[];
  vehicles: Vehicle[];
  accounts: BankAccount[];
}

const DUPLICATE_NOTE = 'Тот же объект есть в другом документе или уже в деле';

export const PropertyDocumentsImport = forwardRef<
  ImportHandle,
  {
    caseId: string;
    property: { realty: Realty[]; vehicles: Vehicle[]; accounts: BankAccount[] };
    debtorName: string;
    debtorInn: string;
    readOnly: boolean;
    onAccept: (accepted: AcceptedProperty) => void;
    /** Встроенный режим (import-handle.ts): документы уже разобраны, кнопки — общие */
    preloaded?: Preloaded<PropertyImport>;
  }
>(function PropertyDocumentsImport({ caseId, property, debtorName, debtorInn, readOnly, onAccept, preloaded }, ref) {
  const embedded = Boolean(preloaded);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<PropertyImport[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [duplicates, setDuplicates] = useState<Set<string>>(new Set());
  const [accepted, setAccepted] = useState<string | null>(null);

  const existingKeys = () => [
    ...property.realty.filter((r) => r.cadastralNumber).map((r) => `realty:${r.cadastralNumber}`),
    ...property.vehicles.flatMap((v) => [v.registrationNumber, v.identificationNumber].filter(Boolean).map((k) => `vehicle:${k}`)),
    ...property.accounts.filter((a) => a.accountNumber).map((a) => `account:${a.accountNumber}`),
  ];

  const load = async (files: File[]) => {
    setBusy(true);
    setErrors([]);
    setResults([]);
    setAccepted(null);
    const existing = existingKeys();
    const loaded: PropertyImport[] = [];
    const failed: string[] = [];

    for (const file of files) {
      try {
        loaded.push(await api.importPropertyDocument(caseId, file, existing, debtorName));
      } catch (e) {
        failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }
    ingest(loaded, failed);
  };

  /** Разобранные выписки → строки с отметками; первая копия объекта отмечена, повторы — нет. */
  const ingest = (loaded: PropertyImport[], failed: string[]) => {
    loaded = [...loaded].sort((a, b) => PRIORITY[a.kind] - PRIORITY[b.kind]);

    // Первая копия каждого объекта отмечена, следующие — нет
    const seen = new Set<string>();
    const all: string[] = [];
    const flagged = new Set<string>();
    const claim = (key: string, identities: string[]) => {
      all.push(key);
      const ids = identities.filter(Boolean);
      if (ids.some((id) => seen.has(id))) flagged.add(key);
      ids.forEach((id) => seen.add(id));
    };
    loaded.forEach((result, r) => {
      result.realty.forEach((row, i) => claim(`${r}:realty:${i}`, [row.realty.cadastralNumber && `realty:${row.realty.cadastralNumber}`]));
      result.vehicles.forEach((row, i) =>
        claim(`${r}:vehicle:${i}`, [row.vehicle.registrationNumber, row.vehicle.identificationNumber].filter(Boolean).map((k) => `vehicle:${k}`)),
      );
      result.accounts.forEach((row, i) => claim(`${r}:account:${i}`, [row.account.accountNumber ? `account:${row.account.accountNumber}` : '']));
    });

    setResults(loaded);
    setDuplicates(flagged);
    setSelected(new Set(all.filter((key) => !flagged.has(key))));
    setErrors(failed);
    setBusy(false);
  };

  const toggle = (key: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const chosen: AcceptedProperty = {
    realty: results.flatMap((result, r) => result.realty.filter((_, i) => selected.has(`${r}:realty:${i}`)).map((row) => row.realty)),
    vehicles: results.flatMap((result, r) => result.vehicles.filter((_, i) => selected.has(`${r}:vehicle:${i}`)).map((row) => row.vehicle)),
    accounts: results.flatMap((result, r) => result.accounts.filter((_, i) => selected.has(`${r}:account:${i}`)).map((row) => row.account)),
  };
  const count = chosen.realty.length + chosen.vehicles.length + chosen.accounts.length;

  const accept = () => {
    onAccept(chosen);
    const summary = [
      `Добавлено: недвижимость — ${chosen.realty.length}, транспорт — ${chosen.vehicles.length}, счета — ${chosen.accounts.length}.`,
      chosen.vehicles.length ? 'Укажите место хранения транспорта.' : '',
    ]
      .filter(Boolean)
      .join(' ');
    setAccepted(summary);
    setResults([]);
    return summary;
  };

  // Встроенный режим: выписки приходят разобранными, внесение — общей кнопкой
  useEffect(() => {
    if (preloaded) ingest(preloaded.results, preloaded.errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- разбор приходит один раз
  }, [preloaded]);
  useImperativeHandle(ref, () => ({ accept: () => (count ? accept() : null) }));

  const debtor = debtorInn.replace(/\s/g, '');
  const check = (key: string, label: string) => (
    <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} aria-label={`Добавить ${label}`} />
  );
  const notes = (key: string, list: string[]) =>
    [...(duplicates.has(key) ? [DUPLICATE_NOTE] : []), ...list].map((note) => (
      <div key={note} className="warning">
        {note}
      </div>
    ));

  return (
    <div className="report-import">
      {!embedded && (
        <DocumentUpload
          label="Загрузить выписки об имуществе"
          busyLabel="Читаю выписки…"
          hint="Выписки ЕГРН (ФНС и Росреестр), сведения ГИБДД, счета из ФНС. Можно несколько файлов сразу. Файлы нигде не сохраняются."
          busy={busy}
          disabled={readOnly}
          onFiles={(files) => void load(files)}
        />
      )}

      {errors.map((error, i) => (
        <p key={i} className="error-text">
          {error}
        </p>
      ))}
      {accepted && <p className="hint">{accepted}</p>}

      {results.length > 0 && (
        <div className="draft">
          {results.map((result, r) => (
            <div key={r} className="import-document">
              <p>
                <strong>{result.title}</strong>
                {result.subject && (
                  <>
                    {' '}— {result.subject.fullName}
                    {result.subject.inn && `, ИНН ${result.subject.inn}`}
                  </>
                )}
              </p>
              {result.subject?.inn && debtor && result.subject.inn !== debtor && (
                <p className="error-text">ИНН в документе не совпадает с ИНН должника в деле. Проверьте, тот ли документ загружен.</p>
              )}

              {result.realty.length > 0 && (
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Недвижимость</th>
                      <th>Доля</th>
                      <th>Основание приобретения</th>
                      <th>Кадастровая стоимость, ₽</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.realty.map((row, i) => {
                      const key = `${r}:realty:${i}`;
                      const x = row.realty;
                      return (
                        <tr key={x.id}>
                          <td>{check(key, x.cadastralNumber || x.address)}</td>
                          <td>
                            {x.kind === 'other' ? x.kindOther || 'Иное' : REALTY_TITLES[x.kind]}, {x.cadastralNumber}
                            <div className="hint">
                              {x.address}
                              {x.area ? `, ${x.area} кв. м` : ''}
                            </div>
                            {x.pledge && <div className="hint">Залог: {x.pledge}</div>}
                            {notes(key, row.notes)}
                          </td>
                          <td>{x.ownership === 'shared' ? x.share || 'долевая' : x.ownership === 'joint' ? 'совместная' : 'единоличная'}</td>
                          <td className="hint">{x.acquisitionBasis || '—'}</td>
                          <td style={{ textAlign: 'right' }}>{row.cadastralValue ? money(row.cadastralValue).digitsWithKopecks : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {result.vehicles.length > 0 && (
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Транспорт</th>
                      <th>Год</th>
                      <th>Госномер</th>
                      <th>VIN / номер кузова</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.vehicles.map((row, i) => {
                      const key = `${r}:vehicle:${i}`;
                      const v = row.vehicle;
                      return (
                        <tr key={v.id}>
                          <td>{check(key, `${v.model} ${v.registrationNumber ?? ''}`.trim())}</td>
                          <td>
                            {v.model}
                            {notes(key, row.notes)}
                          </td>
                          <td>{v.year ?? '—'}</td>
                          <td>{v.registrationNumber ?? '—'}</td>
                          <td className="hint">{v.identificationNumber || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {result.accounts.length > 0 && (
                <>
                  <p className="warning">Остатка на счетах в этих сведениях нет — впишите его по выпискам банков.</p>
                  <table className="simple-table">
                    <thead>
                      <tr>
                        <th />
                        <th>Банк</th>
                        <th>Вид счёта</th>
                        <th>Валюта</th>
                        <th>Открыт</th>
                        <th>Номер</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.accounts.map((row, i) => {
                        const key = `${r}:account:${i}`;
                        const a = row.account;
                        return (
                          <tr key={a.id}>
                            <td>{check(key, `счёт ${a.accountNumber ?? ''}`)}</td>
                            <td>
                              {a.bankName}
                              <div className="hint">{a.bankAddress}</div>
                              {notes(key, [])}
                            </td>
                            <td>{a.accountKind || '—'}</td>
                            <td>{a.currency}</td>
                            <td>{ruDate(a.openedAt)}</td>
                            <td className="hint">{a.accountNumber}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              {result.realty.length + result.vehicles.length + result.accounts.length === 0 && (
                <p className="hint">Нового имущества в документе нет.</p>
              )}

              {/* Возможные сделки за три года — на виду, не в свёрнутом списке */}
              {result.skipped
                .filter((s) => s.warning)
                .map((s, i) => (
                  <p key={i} className="warning">
                    {s.label}: {s.reason}
                  </p>
                ))}
              {result.skipped.some((s) => !s.warning) && (
                <details>
                  <summary className="hint">Не предлагается: {result.skipped.filter((s) => !s.warning).length}</summary>
                  <ul className="hint">
                    {result.skipped
                      .filter((s) => !s.warning)
                      .map((s, i) => (
                        <li key={i}>
                          {s.label} — {s.reason}
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>
          ))}

          {!embedded && (
            <div className="toolbar">
              <button type="button" className="primary" disabled={count === 0} onClick={accept}>
                Добавить отмеченные ({count})
              </button>
              <button type="button" className="link-button" onClick={() => setResults([])}>
                Отмена
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
